import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert
} from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';

const AddMedicine = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // Check permission on component mount
  useEffect(() => {
    if (user && user.role !== 'admin' && !user.permissions?.addMedicine) {
      navigate('/medicines');
    }
  }, [user, navigate]);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'Tablet',
    dosageInstruction: {
      morning: false,
      afternoon: false,
      evening: false,
      night: false,
      beforeFood: false,
      afterFood: false
    },
    stock: 0,
    unit: 'Tabs',
    dosageStrength: '',
    dosageUnit: 'mg',
    manufacturer: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { name, type, dosageInstruction, stock, unit, dosageStrength, dosageUnit, manufacturer } = formData;
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const onChangeNumber = e => {
    setFormData({ ...formData, [e.target.name]: Number(e.target.value) });
  };
  
  const onChangeDosage = e => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      dosageInstruction: {
        ...dosageInstruction,
        [name]: checked
      }
    });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validate form
      if (!name || !type) {
        setError('Please fill all required fields');
        setLoading(false);
        return;
      }
      
      // Format the name with dosage strength if provided
      const formattedData = {...formData};
      if (dosageStrength && dosageUnit) {
        formattedData.name = `${name} ${dosageStrength}${dosageUnit}`;
      }
      
      const res = await axios.post('/api/medicines.php', formattedData);
      
      setLoading(false);
      navigate('/medicines');
    } catch (err) {
      setError(err.response?.data.msg || 'Something went wrong');
      setLoading(false);
    }
  };
  
  const medicineTypes = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Drip', 'Ointment', 'Other'];
  const unitOptions = ['Tabs', 'Caps', 'ml', 'Bottle', 'Box', 'Pcs', 'Vial', 'Ampule'];
  const dosageUnitOptions = ['mg', 'g', 'ml', 'mcg', '%', 'IU', 'mEq'];
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Add New Medicine</h2>
        <Link to="/medicines">
          <Button variant="outline-secondary">
            <FaTimes className="me-2" /> Cancel
          </Button>
        </Link>
      </div>
      
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={onSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Medicine Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={name}
                    onChange={onChange}
                    placeholder="Enter medicine name"
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type *</Form.Label>
                  <Form.Select
                    name="type"
                    value={type}
                    onChange={onChange}
                    required
                  >
                    {medicineTypes.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dosage Strength</Form.Label>
                  <Form.Control
                    type="text"
                    name="dosageStrength"
                    value={dosageStrength}
                    onChange={onChange}
                    placeholder="e.g. 500"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dosage Unit</Form.Label>
                  <Form.Select
                    name="dosageUnit"
                    value={dosageUnit}
                    onChange={onChange}
                  >
                    {dosageUnitOptions.map((unit, index) => (
                      <option key={index} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Manufacturer</Form.Label>
                  <Form.Control
                    type="text"
                    name="manufacturer"
                    value={manufacturer}
                    onChange={onChange}
                    placeholder="Enter manufacturer"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <h5 className="mt-4 mb-3">Default Dosage Instructions</h5>
            
            <Row className="mb-3">
              <Col xs={3}>
                <Form.Check
                  type="checkbox"
                  id="morning"
                  name="morning"
                  label="Morning"
                  checked={dosageInstruction.morning}
                  onChange={onChangeDosage}
                />
              </Col>
              <Col xs={3}>
                <Form.Check
                  type="checkbox"
                  id="afternoon"
                  name="afternoon"
                  label="Afternoon"
                  checked={dosageInstruction.afternoon}
                  onChange={onChangeDosage}
                />
              </Col>
              <Col xs={3}>
                <Form.Check
                  type="checkbox"
                  id="evening"
                  name="evening"
                  label="Evening"
                  checked={dosageInstruction.evening}
                  onChange={onChangeDosage}
                />
              </Col>
              <Col xs={3}>
                <Form.Check
                  type="checkbox"
                  id="night"
                  name="night"
                  label="Night"
                  checked={dosageInstruction.night}
                  onChange={onChangeDosage}
                />
              </Col>
            </Row>
            
            <Row className="mb-4">
              <Col xs={6}>
                <Form.Check
                  type="checkbox"
                  id="beforeFood"
                  name="beforeFood"
                  label="Before Food"
                  checked={dosageInstruction.beforeFood}
                  onChange={onChangeDosage}
                />
              </Col>
              <Col xs={6}>
                <Form.Check
                  type="checkbox"
                  id="afterFood"
                  name="afterFood"
                  label="After Food"
                  checked={dosageInstruction.afterFood}
                  onChange={onChangeDosage}
                />
              </Col>
            </Row>
            
            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
              >
                <FaSave className="me-2" /> {loading ? 'Saving...' : 'Save Medicine'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddMedicine; 
